import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend, LineChart, Line } from "recharts";
import { ArrowLeft, ChartBar } from "@phosphor-icons/react";

export default function ObservatoryDashboard() {
  const [data, setData] = useState(null);
  const [taxo, setTaxo] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/observatory/dashboard").then((r) => setData(r.data)),
      api.get("/observatory/taxonomy").then((r) => setTaxo(r.data)),
    ]);
  }, []);

  if (!data || !taxo) return <div className="p-12 label-mono">Chargement...</div>;

  // Aggregate ratios by report (or by country)
  const ratios = ["dette_pib", "service_dette_recettes", "deficit_pib", "croissance_pib", "inflation"];
  const RATIO_LABELS = {
    dette_pib: "Dette / PIB",
    service_dette_recettes: "Service / Recettes",
    deficit_pib: "Déficit / PIB",
    croissance_pib: "Croissance PIB",
    inflation: "Inflation",
    ide_pib: "IDE / PIB",
  };

  const series = data.reports.map((r) => ({
    name: `${r.organisation} ${r.annee}`,
    titre: r.titre,
    pays: r.pays,
    ...(r.analyse?.ratios || {}),
  }));

  // Vulnerability score per report
  const vulnerability = (r) => {
    const dp = (r.dette_pib || 0);
    const sr = (r.service_dette_recettes || 0);
    const df = (r.deficit_pib || 0);
    return dp * 0.5 + sr * 0.3 + df * 0.2; // pondération simple
  };

  const sorted = [...series].map((r) => ({ ...r, vuln: vulnerability(r) })).sort((a, b) => b.vuln - a.vuln);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/observatory" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="vuln-back">
        <ArrowLeft size={14} /> Retour à la veille
      </Link>

      <div className="mb-10 fade-up">
        <div className="label-mono">§ Vita-Observatory · Vulnérabilité</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
          <ChartBar size={40} className="text-[var(--signal)]" /> Dashboard de vulnérabilité
        </h1>
        <p className="mt-3 text-[var(--muted)] max-w-2xl">Ratios extraits par IA depuis vos {data.count} rapports analysés. Score composite = (Dette/PIB × 0.5) + (Service/Recettes × 0.3) + (Déficit/PIB × 0.2).</p>
      </div>

      {data.count === 0 ? (
        <div className="border border-black/15 p-16 text-center">
          <ChartBar size={56} weight="duotone" className="mx-auto text-[var(--muted)]" />
          <h2 className="mt-4 font-serif text-2xl">Aucune analyse disponible</h2>
          <p className="mt-2 text-[var(--muted)]">Importez et analysez des rapports pour alimenter le dashboard.</p>
          <Link to="/observatory" className="mt-6 inline-flex border border-black px-5 py-2 label-mono hover:bg-black hover:text-[var(--paper)]">+ Importer un rapport</Link>
        </div>
      ) : (
        <>
          {/* Vulnerability ranking */}
          <div className="border border-black/15 bg-[var(--paper)] p-6 mb-8">
            <div className="label-mono">§ Classement vulnérabilité</div>
            <h2 className="font-serif text-2xl mt-1">Score composite</h2>
            <div className="mt-6 h-72">
              <ResponsiveContainer>
                <BarChart data={sorted.slice(0, 12)} margin={{ top: 10, right: 10, bottom: 30, left: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #000", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                  <Bar dataKey="vuln" radius={0}>
                    {sorted.map((d, i) => (
                      <Cell key={i} fill={d.vuln >= 70 ? "#7A0F0F" : d.vuln >= 50 ? "#D92D20" : d.vuln >= 30 ? "#DC6803" : "#205D3B"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-ratio breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15 mb-8">
            {ratios.map((rk) => (
              <div key={rk} className="bg-[var(--paper)] p-6">
                <div className="label-mono">{RATIO_LABELS[rk]}</div>
                <div className="mt-4 h-56">
                  <ResponsiveContainer>
                    <BarChart data={series} margin={{ top: 5, right: 10, bottom: 30, left: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} angle={-30} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #000", borderRadius: 0, fontSize: 11 }} />
                      <Bar dataKey={rk} fill="#D92D20" radius={0} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="border border-black/15 bg-[var(--paper)] p-6">
            <div className="label-mono">§ Détails par rapport</div>
            <table className="w-full mt-4 text-sm">
              <thead className="border-b border-black/15 label-mono text-left">
                <tr>
                  <th className="p-3">Rapport</th>
                  <th className="p-3">Pays</th>
                  {ratios.map((r) => <th key={r} className="p-3 text-right">{RATIO_LABELS[r]}</th>)}
                  <th className="p-3 text-right">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {sorted.map((r, i) => (
                  <tr key={i} className="hover:bg-[var(--paper-2)]">
                    <td className="p-3 font-serif truncate max-w-xs">{r.titre}</td>
                    <td className="p-3 font-mono text-xs">{r.pays}</td>
                    {ratios.map((rk) => <td key={rk} className="p-3 text-right tabular-nums">{Number(r[rk] || 0).toFixed(1)}%</td>)}
                    <td className="p-3 text-right tabular-nums font-medium" style={{ color: r.vuln >= 70 ? "#7A0F0F" : r.vuln >= 50 ? "#D92D20" : r.vuln >= 30 ? "#DC6803" : "#205D3B" }}>{r.vuln.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
